import { useMemo, useState } from 'react';
import { downloadOutcomeExport } from '@/api';
import type { SkillDef, PipelineNode, PipelineEdge, GroupNode } from '@/mocks/pipeline';

type Props = {
  onClose: () => void;
  pipelineName: string;
  description: string;
  nodes: PipelineNode[];
  edges: PipelineEdge[];
  groups: GroupNode[];
  skills: SkillDef[];
  score?: string;
  lastRun?: string;
  outcomeId?: string | null;
};

function skillFrontmatter(name: string, description: string, score?: string): string {
  const lines = ['---', `name: ${name}`, `description: ${description || name}`];
  if (score) lines.push(`quality_score: ${score}`);
  lines.push('---', '');
  return lines.join('\n');
}

function buildSkillFiles(props: Props): { path: string; content: string }[] {
  const skillMap = Object.fromEntries(props.skills.map((s) => [s.id, s]));
  const seen = new Set<string>();
  const files: { path: string; content: string }[] = [];
  for (const n of props.nodes) {
    if (!n.skillId || seen.has(n.skillId)) continue;
    seen.add(n.skillId);
    const skill = skillMap[n.skillId];
    const name = skill?.name || n.skillId;
    const body = (n.body || skill?.body || skill?.description || '').trim();
    files.push({
      path: `skills/${n.skillId}/SKILL.md`,
      content: skillFrontmatter(name, skill?.description || name, props.score) + (body ? body + '\n' : ''),
    });
  }
  return files;
}

function buildOutcomeMd(props: Props): string {
  const { pipelineName, description, nodes, edges, groups, skills, score, lastRun } = props;
  const skillMap = Object.fromEntries(skills.map((s) => [s.id, s]));
  const lines: string[] = [];
  lines.push(`# ${pipelineName}`);
  lines.push('');
  if (description) {
    lines.push(`**Description:** ${description}`);
    lines.push('');
  }
  if (score) lines.push(`**Score:** ${score}`);
  lines.push(`**Steps:** ${nodes.length}`);
  if (lastRun) lines.push(`**Last run:** ${lastRun}`);
  lines.push('');
  lines.push('## Pipeline');
  nodes.forEach((n, i) => {
    const skill = skillMap[n.skillId];
    const incoming = edges.filter((e) => e.to === n.id).map((e) => e.field);
    const name = skill?.name || n.skillId;
    const inStr = incoming.length ? ` ← ${incoming.join(', ')}` : '';
    lines.push(`${i + 1}. ${name}${inStr}`);
  });
  if (groups.length) {
    lines.push('');
    lines.push('## Groups');
    groups.forEach((g) => {
      const members = nodes.filter((n) => {
        const cx = n.x + 110, cy = n.y + 46;
        return cx >= g.x && cx <= g.x + g.w && cy >= g.y && cy <= g.y + g.h;
      });
      lines.push(`- ${g.type === 'loop' ? 'Loop' : 'Process'}: ${g.title} (${members.length} members${g.type === 'loop' ? `, ${g.iterations || 1} iterations` : ''})`);
    });
  }
  lines.push('');
  return lines.join('\n');
}

export default function ExportModal(props: Props) {
  const { onClose } = props;
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const md = useMemo(() => buildOutcomeMd(props), [props]);
  const skillFiles = useMemo(() => buildSkillFiles(props), [props]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 1600);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(md);
      showToast('Copied!');
    } catch {
      showToast('Copy failed');
    }
  };

  const download = async () => {
    if (props.outcomeId) {
      setBusy(true);
      try {
        await downloadOutcomeExport(props.outcomeId);
        showToast('Downloaded skillset.zip');
      } catch {
        showToast('Export failed');
      } finally {
        setBusy(false);
      }
      return;
    }
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Outcome.md';
    a.click();
    URL.revokeObjectURL(url);
    showToast('Downloaded Outcome.md');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="w-[640px] max-w-[92vw] max-h-[80vh] bg-surface border border-border rounded-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <i className="ri-file-download-line text-accent" />
            <h3 className="text-sm font-semibold text-textp">Export skillset</h3>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center text-texts hover:text-textp rounded-md hover:bg-surface2">
            <i className="ri-close-line" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4 text-[12px] font-mono text-textp bg-bg m-3 rounded-lg border border-border">
          <pre className="whitespace-pre-wrap">{md}</pre>
          {skillFiles.map((f) => (
            <pre key={f.path} className="mt-4 pt-4 border-t border-border whitespace-pre-wrap">
              {`# ${f.path}\n\n${f.content}`}
            </pre>
          ))}
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
          <button onClick={copy} className="flex items-center gap-1.5 bg-surface2 hover:bg-border text-textp text-sm px-3 py-1.5 rounded-md whitespace-nowrap">
            <i className="ri-clipboard-line" /> Copy Outcome.md
          </button>
          <button onClick={download} disabled={busy} className="flex items-center gap-1.5 bg-accent hover:bg-accent-hover text-accent-text text-sm px-3 py-1.5 rounded-md whitespace-nowrap disabled:opacity-60">
            <i className="ri-download-line" /> {props.outcomeId ? 'Download .zip' : 'Download .md'}
          </button>
        </div>
        {toast && (
          <div className="absolute bottom-16 left-1/2 -translate-x-1/2 bg-textp text-bg text-xs font-medium px-3 py-1.5 rounded-md">
            {toast}
          </div>
        )}
      </div>
    </div>
  );
}
